import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useMatch } from '../context/MatchContext';
import Header from './Header';
import VideoPlayer from './VideoPlayer';
import ActionComposer from './ActionComposer';
import KeybindingsEditor from './KeybindingsEditor';
import { loadSet2Bindings, buildKeyMap } from '../utils/keybindings';
import { QRCodeSVG } from 'qrcode.react';
import TimelinePanel from './TimelinePanel';
import MatchSetup from './MatchSetup';
import { useTimer } from '../hooks/useTimer';
import { useVideoSync } from '../hooks/useVideoSync';
import { saveAction, savePenalty, deleteAction, deletePenalty, updateAction, createSession, exportSession, importSession } from '../services/api';
import { clearSavedSession } from '../context/MatchContext';
import { calculatePoints } from '../utils/scoring';
import { formatDisplay } from '../hooks/useTimer';

const API_SYNC = '/api/sync.php';

function MainLayout() {
  const { state, setupComplete, confirmAction, deleteAction: ctxDeleteAction,
          updateAction: ctxUpdateAction, loadActionForEdit, clearEditMode, setActionField,
          addPenalty, deletePenalty: ctxDeletePenalty, setTimer, setVideoTimestamp,
          setRound, loadSession, markRound, deleteRoundMarker } = useMatch();
  const { isSetupComplete, session } = state;

  // Modalità di lavoro: 'single' | 'dual-pc' | 'ipad'
  const [workMode, setWorkMode] = useState('single');

  // IP locale del server (per URL accessibile dall'iPad sulla stessa rete WiFi)
  const [localIp, setLocalIp]       = useState(null);
  const [manualIp, setManualIp]     = useState('');
  const [showIpInput, setShowIpInput] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') { setLocalIp(host); return; }

    // 1. Prova backend PHP (funziona in produzione)
    fetch('/api/localip.php')
      .then(r => r.json())
      .then(d => { if (d.ip && d.ip !== '127.0.0.1' && d.ip !== '::1') setLocalIp(d.ip); })
      .catch(() => {});

    // 2. Prova WebRTC (funziona in dev)
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pc.createDataChannel('');
      pc.createOffer().then(o => pc.setLocalDescription(o)).catch(() => {});
      const seen = new Set();
      pc.onicecandidate = (e) => {
        if (!e.candidate) return;
        const matches = e.candidate.candidate.matchAll(/(\d+\.\d+\.\d+\.\d+)/g);
        for (const m of matches) {
          const ip = m[1];
          if (seen.has(ip)) continue;
          seen.add(ip);
          // Preferisci IP di rete locale tipici
          if (/^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(ip)) {
            setLocalIp(ip);
            pc.close();
            return;
          }
        }
      };
      setTimeout(() => pc.close(), 4000);
    } catch {}
  }, []);

  // Modalità tastiera e editor shortcut
  const [kbMode,       setKbMode]       = useState('set1');
  const [showKbEditor, setShowKbEditor] = useState(false);
  const [set2Bindings, setSet2Bindings] = useState(() => loadSet2Bindings());

  const videoWindowRef = useRef(null);
  const bcStateRef     = useRef(null);   // BroadcastChannel per broadcast stato scoreboard
  const videoRef       = useRef(null);
  const playerRef      = useRef(null);

  const handleTimerTick = useCallback((s) => {
    setTimer({ seconds: s, display: formatDisplay(s) });
  }, [setTimer]);

  const timer = useTimer(handleTimerTick);

  const isPlayingRef = useRef(false);

  const videoSync = useVideoSync({
    onPause: useCallback(() => { isPlayingRef.current = false; timer.pause(); }, [timer]),
    onPlay:  useCallback(() => { isPlayingRef.current = true;  timer.start(); }, [timer]),
  });

  const handlePlayerReady = useCallback((player) => {
    playerRef.current = player;
    videoSync.registerPlayer(player);
  }, [videoSync]);

  const handleVideoPlay  = useCallback(() => {
    isPlayingRef.current = true;
    timer.setTime(videoSync.getCurrentTime());
    timer.start();
  }, [timer, videoSync]);
  const handleVideoPause = useCallback(() => {
    isPlayingRef.current = false;
    timer.pause();
  }, [timer]);
  const handleVideoTimeUpdate = useCallback((time) => {
    setVideoTimestamp(time);
    timer.setTime(time);
  }, [setVideoTimestamp, timer]);

  // ── Gestione cambio modalità ──────────────────────────────────────────────
  useEffect(() => {
    if (workMode === 'dual-pc') {
      // Apre BroadcastChannel per ricevere play/pause/timeupdate dalla finestra video
      videoSync.initDualChannel();
      // Canale separato per trasmettere lo stato dello scoreboard alla finestra video
      if (!bcStateRef.current) {
        bcStateRef.current = new BroadcastChannel('tkd-sync');
        // Ricevi tasti forwarded dalla finestra video (window 2)
        bcStateRef.current.onmessage = (e) => {
          if (e.data?.type === 'keydown') {
            window.dispatchEvent(new KeyboardEvent('keydown', { key: e.data.key, bubbles: true }));
          }
        };
      }
    } else {
      videoSync.closeDualChannel();
      if (bcStateRef.current) {
        bcStateRef.current.close();
        bcStateRef.current = null;
      }
    }
  }, [workMode, videoSync]);

  // ── Keyboard shortcuts video (funzionano anche da finestra 1) ───────────
  const videoSyncRef     = useRef(videoSync);
  const set2BindingsRef  = useRef(set2Bindings);
  useEffect(() => { videoSyncRef.current = videoSync; });
  useEffect(() => { set2BindingsRef.current = set2Bindings; });

  useEffect(() => {
    const handler = (e) => {
      try {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        const vs       = videoSyncRef.current;
        const keyMap   = buildKeyMap(set2BindingsRef.current);
        const actionId = keyMap[e.code];
        if (actionId === 'video_play_pause') {
          e.preventDefault();
          isPlayingRef.current ? vs.sendPause() : vs.sendPlay();
          isPlayingRef.current = !isPlayingRef.current;
        } else if (actionId === 'video_seek_fwd') {
          e.preventDefault();
          vs.sendSeek(vs.getCurrentTime() + (e.shiftKey ? 5 : 1));
        } else if (actionId === 'video_seek_back') {
          e.preventDefault();
          vs.sendSeek(Math.max(0, vs.getCurrentTime() - (e.shiftKey ? 5 : 1)));
        } else if (actionId === 'video_frame_back' || actionId === 'video_frame_fwd') {
          e.preventDefault();
          // In dual-pc forwarda il keydown a Window 2 che ha il player locale
          // (evita problemi di getCurrentTime stale via BC)
          if (bcStateRef.current) {
            bcStateRef.current.postMessage({ type: 'keydown', key: actionId === 'video_frame_back' ? ',' : '.' });
          } else {
            vs.sendSeek(actionId === 'video_frame_back' ? Math.max(0, vs.getCurrentTime() - 0.04) : vs.getCurrentTime() + 0.04);
          }
        }
      } catch {}
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // registrato una sola volta, legge sempre videoSync e bindings aggiornati via ref

  // ── Broadcast stato scoreboard ogni 500ms (Modalità dual-pc) ────────────
  useEffect(() => {
    if (workMode !== 'dual-pc') return;
    const sendState = () => {
      bcStateRef.current?.postMessage({
        type: 'state',
        session: state.session,
        scores: state.scores,
        timer: { display: timer.display, isRunning: timer.isRunning },
        currentRound: state.currentRound,
        roundMarkers: state.roundMarkers,
      });
    };
    sendState(); // Invio immediato
    const interval = setInterval(sendState, 500);
    return () => clearInterval(interval);
  }, [workMode, state.session, state.scores, timer.display, timer.isRunning, state.currentRound, state.roundMarkers]);

  // ── Post sessione su sync.php al cambio modalità iPad ────────────────────
  useEffect(() => {
    if (workMode !== 'ipad' || !isSetupComplete) return;
    fetch(API_SYNC, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session:      state.session,
        scores:       state.scores,
        currentRound: state.currentRound,
      }),
    }).catch(() => {});
  }, [workMode, isSetupComplete, state.session, state.scores, state.currentRound]);

  // ── Post timer su sync.php (Modalità iPad) ───────────────────────────────
  useEffect(() => {
    if (workMode !== 'ipad') return;
    fetch(API_SYNC, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        timerSeconds: state.timer.seconds,
        timerDisplay: state.timer.display,
        timerRunning: timer.isRunning,
      }),
    }).catch(() => {});
  }, [workMode, state.timer.display, state.timer.seconds, timer.isRunning]);

  // ── Apri finestra video ───────────────────────────────────────────────────
  const handleOpenVideoWindow = useCallback(() => {
    const mode = workMode === 'ipad' ? 'ipad' : 'dual';
    const videoUrl = session?.video_source || '';
    if (videoUrl) localStorage.setItem('tkd_dual_video_url', videoUrl);
    const url  = `${window.location.origin}/video?mode=${mode}`;
    if (videoWindowRef.current && !videoWindowRef.current.closed) {
      videoWindowRef.current.focus();
    } else {
      videoWindowRef.current = window.open(url, 'tkd-video', 'width=1280,height=800,menubar=no,toolbar=no,location=no');
    }
  }, [workMode, session]);

  const handleWorkModeChange = useCallback((mode) => {
    setWorkMode(mode);
  }, []);

  // ── Callback analisi (identici a prima) ──────────────────────────────────
  const handleAthleteSelect = useCallback(() => {
    timer.pause();
    videoSync.sendPause();
    const t = videoSync.getCurrentTime();
    setVideoTimestamp(t);
  }, [timer, videoSync, setVideoTimestamp]);

  const handleConfirmAction = useCallback(async (actionData) => {
    const pts = calculatePoints(
      actionData.technique,
      actionData.target,
      actionData.outcome,
      actionData.technicalBonus
    );

    // ── Modalità modifica ────────────────────────────────────────────────────
    if (state.editingActionId) {
      const fields = {
        athlete:          actionData.athlete,
        action_type:      actionData.actionType,
        technique:        actionData.technique,
        custom_technique: actionData.customTechnique || null,
        leg_side:         actionData.legSide,
        leg_position:     actionData.legPosition,
        guard:            actionData.guard,
        distance:         actionData.distance,
        target:           actionData.target,
        outcome:          actionData.outcome,
        points:           pts,
        technical_bonus:  actionData.technicalBonus ? 1 : 0,
        timestamp_start:  actionData.timestamp_start ?? null,
        timestamp_end:    actionData.timestamp_end   ?? null,
      };
      ctxUpdateAction(state.editingActionId, fields);
      clearEditMode();
      if (session?.id) {
        try { await updateAction(state.editingActionId, fields); } catch (e) { console.error(e); }
      }
      timer.start();
      videoSync.sendPlay();
      return;
    }

    // ── Nuova azione ─────────────────────────────────────────────────────────
    const timestamp = videoSync.getCurrentTime();
    const fullAction = {
      ...actionData,
      id: Date.now(),
      session_id: session?.id,
      round_number: state.currentRound,
      timestamp_video:   parseFloat(timestamp.toFixed(1)),
      timestamp_display: formatDisplay(timestamp),
      points: pts,
    };

    confirmAction(fullAction);

    if (session?.id) {
      try {
        await saveAction({
          session_id:        session.id,
          round_number:      state.currentRound,
          timestamp_video:   fullAction.timestamp_video,
          timestamp_display: fullAction.timestamp_display,
          athlete:           fullAction.athlete,
          action_type:       fullAction.actionType,
          technique:         fullAction.technique,
          custom_technique:  fullAction.customTechnique || null,
          leg_side:          fullAction.legSide         || null,
          leg_position:      fullAction.legPosition     || null,
          guard:             fullAction.guard           || null,
          distance:          fullAction.distance        || null,
          target:            fullAction.target          || null,
          outcome:           fullAction.outcome,
          points:            pts,
          technical_bonus:   fullAction.technicalBonus ? 1 : 0,
          sequence_id:       fullAction.sequence_id     || null,
        });
      } catch (e) { console.error('Errore salvataggio azione:', e); }
    }

    timer.start();
    videoSync.sendPlay();
  }, [videoSync, session, state.currentRound, state.editingActionId, confirmAction, ctxUpdateAction, clearEditMode, timer]);

  const handleCancelAction = useCallback(() => {
    clearEditMode();
    timer.start();
    videoSync.sendPlay();
  }, [clearEditMode, timer, videoSync]);

  const handleDeleteAction = useCallback(async (actionId) => {
    ctxDeleteAction(actionId);
    if (session?.id) {
      try { await deleteAction(actionId); } catch (e) { console.error(e); }
    }
  }, [ctxDeleteAction, session]);

  const handleLoadForEdit = useCallback((action) => {
    loadActionForEdit(action);
    // Seek al timestamp dell'azione per posizionare il video
    if (action.timestamp_video) videoSync.sendSeek(action.timestamp_video);
  }, [loadActionForEdit, videoSync]);

  const handleMarkStart = useCallback(() => {
    const t = parseFloat(videoSync.getCurrentTime().toFixed(1));
    setActionField('timestamp_start', t);
  }, [videoSync, setActionField]);

  const handleMarkEnd = useCallback(() => {
    const t = parseFloat(videoSync.getCurrentTime().toFixed(1));
    setActionField('timestamp_end', t);
  }, [videoSync, setActionField]);

  const handleAddPenalty = useCallback(async (penaltyData) => {
    const timestamp = videoSync.getCurrentTime();
    const pen = {
      ...penaltyData,
      id: Date.now(),
      session_id: session?.id,
      round_number: state.currentRound,
      timestamp_video:   parseFloat(timestamp.toFixed(1)),
      timestamp_display: formatDisplay(timestamp),
    };
    addPenalty(pen);
    if (session?.id) {
      try {
        await savePenalty({
          session_id:        session.id,
          round_number:      state.currentRound,
          timestamp_video:   pen.timestamp_video,
          timestamp_display: pen.timestamp_display,
          athlete:           pen.athlete,
          penalty_type:      pen.penalty_type,
        });
      } catch (e) { console.error(e); }
    }
  }, [addPenalty, session, state.currentRound, videoSync]);

  const handleDeletePenalty = useCallback(async (penId) => {
    ctxDeletePenalty(penId);
    if (session?.id) {
      try { await deletePenalty(penId); } catch (e) { console.error(e); }
    }
  }, [ctxDeletePenalty, session]);

  const handleSetupComplete = useCallback(async (setupData) => {
    try {
      if (setupData.importedData) {
        const imported = await importSession(setupData.importedData);
        setupComplete(imported.session);
        loadSession(imported.session, imported.actions || [], imported.penalties || []);
        return;
      }
      const sess = await createSession(setupData);
      setupComplete(sess);
    } catch (e) {
      const localSess = { ...setupData, id: null };
      setupComplete(localSess);
    }
  }, [setupComplete, loadSession]);

  const handleExport = useCallback(async () => {
    if (!session?.id) {
      const data = {
        version: '1.0',
        exported_at: new Date().toISOString(),
        session: session,
        actions: state.actions,
        penalties: state.penalties,
        summary: { total_score_blue: state.scores.blue, total_score_red: state.scores.red },
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `tkd_sessione_${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
      return;
    }
    try {
      const data = await exportSession(session.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `tkd_sessione_${session.id}_${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  }, [session, state]);

  const handleMarkRound = useCallback((round, type) => {
    const t = videoSync.getCurrentTime();
    markRound({
      id:                Date.now(),
      round,
      type,
      timestamp_video:   parseFloat(t.toFixed(1)),
      timestamp_display: formatDisplay(t),
    });
  }, [videoSync, markRound]);

  const handleImportTrigger = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const jsonData = JSON.parse(ev.target.result);
          await handleSetupComplete({ importedData: jsonData });
        } catch { alert('File JSON non valido'); }
      };
      reader.readAsText(file);
    };
    input.click();
  }, [handleSetupComplete]);

  // ── Setup non completato ──────────────────────────────────────────────────
  if (!isSetupComplete) {
    return <MatchSetup onComplete={handleSetupComplete} />;
  }

  const networkBase = localIp
    ? `http://${localIp}:${window.location.port}`
    : window.location.origin;
  const ipadUrl  = `${networkBase}/comandi`;
  const videoUrl = `${networkBase}/video?mode=ipad`;

  // ── Pannello analisi (riusabile in Mode 1 e 2) ───────────────────────────
  const AnalysisPanels = (
    <div className="analysis-section">
      <div className="analysis-panel-v2">
        <ActionComposer
          onAthleteSelect={handleAthleteSelect}
          onConfirm={handleConfirmAction}
          onCancel={handleCancelAction}
          onAddPenalty={handleAddPenalty}
          onDeletePenalty={handleDeletePenalty}
          kbMode={kbMode}
          set2Bindings={set2Bindings}
          isEditing={!!state.editingActionId}
          onMarkStart={handleMarkStart}
          onMarkEnd={handleMarkEnd}
        />
        <TimelinePanel
          onDeleteAction={handleDeleteAction}
          onLoadForEdit={handleLoadForEdit}
          onSeek={(t) => videoSync.sendSeek(t)}
        />
      </div>
    </div>
  );

  return (
    <div className={`app-container${workMode !== 'single' ? ' dual-mode' : ''}`}>
      <Header
        timer={timer}
        onNewMatch={() => { clearSavedSession(); window.location.reload(); }}
        onExport={handleExport}
        onImport={handleImportTrigger}
        onRoundChange={setRound}
        onMarkRound={handleMarkRound}
        onDeleteRoundMarker={deleteRoundMarker}
        onSeekToRound={(r) => {
          const startMarker = state.roundMarkers.find(m => m.round === r && m.type === 'start');
          videoSync.sendSeek(startMarker ? startMarker.timestamp_video : 0);
        }}
        workMode={workMode}
        onWorkModeChange={handleWorkModeChange}
        onOpenVideo={handleOpenVideoWindow}
        kbMode={kbMode}
        onKbModeChange={setKbMode}
        onOpenKbEditor={() => setShowKbEditor(true)}
      />

      <div className="main-content">
        {/* ── Modalità 1: Schermo Singolo ─────────────────────────────── */}
        {workMode === 'single' && (
          <>
            <div className="video-section">
              <VideoPlayer
                ref={videoRef}
                initialUrl={session?.video_source || ''}
                onPlayerReady={handlePlayerReady}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onTimeUpdate={handleVideoTimeUpdate}
                videoSync={videoSync}
                actions={state.actions}
                roundMarkers={state.roundMarkers}
                currentRound={state.currentRound}
              />
            </div>
            {AnalysisPanels}
          </>
        )}

        {/* ── Modalità 2: Due Schermi PC ──────────────────────────────── */}
        {workMode === 'dual-pc' && AnalysisPanels}

        {/* ── Modalità 3: PC + iPad ────────────────────────────────────── */}
        {workMode === 'ipad' && (
          <div className="ipad-instructions">
            <div className="ipad-instructions-card">
              <h2>🖥📱 Modalità PC + iPad</h2>
              <p className="text-dim" style={{ marginBottom: 24 }}>
                Segui questi passaggi per iniziare l'analisi:
              </p>

              <div className="instruction-step">
                <span className="step-num">1</span>
                <div className="step-content">
                  <div className="step-title">Su questo PC — Apri la schermata video</div>
                  <div className="step-url">{videoUrl}</div>
                  <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={handleOpenVideoWindow}>
                    🖥 Apri Finestra Video
                  </button>
                </div>
              </div>

              <div className="instruction-step">
                <span className="step-num">2</span>
                <div className="step-content">
                  <div className="step-title">Sul tuo iPad — Apri il pannello comandi</div>
                  <div className="step-url">{ipadUrl}</div>
                  <p className="text-dim" style={{ fontSize: 13, marginTop: 6 }}>
                    Scansiona il QR code con l'iPad oppure apri Safari e naviga a questo URL
                  </p>
                  <div style={{ marginTop: 16, display: 'inline-block', padding: 10, background: '#fff', borderRadius: 8 }}>
                    <QRCodeSVG value={ipadUrl} size={160} />
                  </div>
                </div>
              </div>

              <div className="instruction-note">
                💡 PC e iPad devono essere sulla <strong>stessa rete WiFi</strong>.
                Timer e scoreboard sono sincronizzati in tempo reale.
                {!localIp && (
                  <div style={{ marginTop: 10 }}>
                    {!showIpInput ? (
                      <button
                        onClick={() => setShowIpInput(true)}
                        style={{ background: 'none', border: '1px solid #555', color: 'var(--color-text-sub)', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}
                      >
                        Inserisci IP manualmente
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--color-text-sub)' }}>IP del PC:</span>
                        <input
                          style={{ background: 'var(--bg-card)', border: '1px solid #444', color: 'var(--color-text)', borderRadius: 4, padding: '4px 8px', fontSize: 13, width: 150 }}
                          placeholder="es. 192.168.1.42"
                          value={manualIp}
                          onChange={e => setManualIp(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && manualIp && setLocalIp(manualIp)}
                        />
                        <button
                          onClick={() => manualIp && setLocalIp(manualIp)}
                          style={{ background: 'var(--color-blue)', border: 'none', color: '#fff', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}
                        >
                          Usa
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
                          (trovalo in Impostazioni → WiFi sul PC)
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showKbEditor && (
        <KeybindingsEditor onClose={() => {
          setShowKbEditor(false);
          setSet2Bindings(loadSet2Bindings());
        }} />
      )}
    </div>
  );
}

export default MainLayout;
